/* eslint-disable max-lines-per-function */
import type { SexType } from '@faker-js/faker';
import { faker } from '@faker-js/faker';
import { generateFromEnum, generateInitialPaginationApi } from '@ingenico/api-sdk-core/mocks';
import type {
  ConfigurationResourceItemV2Api,
  DeploymentRequestResourceItemBffV2Api,
  DeploymentRequestResourceOptionConfigurationMinimalBffV2Api,
  DeploymentRequestResourceOptionMinimalV2Api,
  DeploymentRequestResourceTriggerOptionV2Api,
  DeploymentRequestResourceTriggerV2Api,
  EnumDayOfWeekApi,
  SearchConfigurationsV2MutationResponseApi,
  SearchDeploymentRequestsBffV2MutationResponseApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { enumDayOfWeek, enumReferenceModeV2 } from '@ingenico/api-sdk-device-management-private-v2';
import type { PathParams, HttpRequestResolverExtras, ResponseResolverInfo } from 'msw';
import { HttpResponse } from 'msw';

function generateDeploymentRequestResourceTriggerOptionV2Api(
  optional?: Partial<DeploymentRequestResourceTriggerOptionV2Api>,
): DeploymentRequestResourceTriggerOptionV2Api {
  return {
    date: faker.date.future(),
    exclusionDates: faker.helpers.multiple(() => faker.date.future(), { count: 4 }),
    timeSlot: {
      startDate: faker.date.future(),
      endDate: faker.date.future(),
    },
    maxNoOfDeployments: faker.number.int({ min: 20, max: 1000 }),
    daysOfWeek: [
      ...new Set(faker.helpers.multiple(() => generateFromEnum(enumDayOfWeek), { count: 4 })),
    ] as EnumDayOfWeekApi[],
    ...optional,
  };
}

function generateDeploymentRequestResourceTriggerV2Api(
  optional?: Partial<DeploymentRequestResourceTriggerV2Api>,
): DeploymentRequestResourceTriggerV2Api {
  return {
    type: 'SCHEDULED',
    scheduleOption: { ...generateDeploymentRequestResourceTriggerOptionV2Api(optional?.scheduleOption) },
    ...optional,
  };
}

export function generateDeploymentRequestResourceItemBffV2(
  configurationType?: 'BOTH' | 'CONFIGURATION' | 'DELETE',
): DeploymentRequestResourceItemBffV2Api {
  const showOptionsSelection = configurationType ?? faker.helpers.arrayElement(['CONFIGURATION', 'DELETE', 'BOTH']);

  const configuration: DeploymentRequestResourceOptionConfigurationMinimalBffV2Api | undefined = [
    'CONFIGURATION',
    'BOTH',
  ].includes(showOptionsSelection)
    ? {
        snapshotId: faker.string.uuid(),
        reference: {
          description: faker.lorem.paragraph(),
          id: faker.string.uuid(),
          name: faker.commerce.product(),
        },
        type: faker.helpers.arrayElement(Object.values(enumReferenceModeV2)),
      }
    : undefined;

  const deleteDTO: DeploymentRequestResourceOptionMinimalV2Api['delete'] = ['DELETE', 'BOTH'].includes(
    showOptionsSelection,
  )
    ? {
        applicationVersionsIds: [],
        filePaths: [faker.internet.url()],
        packageIds: faker.helpers.multiple(() => faker.string.alphanumeric(16), { count: { min: 1, max: 15 } }),
        systemPackageVersionsIds: [],
      }
    : undefined;

  const sex: SexType = faker.helpers.arrayElement(['female', 'male']);
  return {
    creationDate: faker.date.past(),
    id: faker.string.uuid(),
    name: faker.commerce.product(),
    creator: {
      id: faker.string.uuid(),
      name: `${faker.person.firstName(sex)} ${faker.person.lastName(sex)}`,
      type: 'USER',
    },
    dynamicGroup: {
      deviceCount: faker.number.int(),
      snapshotId: faker.string.uuid(),
      type: 'CUSTOM',
    },
    options: {
      configuration,
      delete: deleteDTO,
    },
    // status: faker.helpers.arrayElement(Object.values(enumDeploymentRequestStatusV2)),
    status: 'PROCESSING',
    trigger: generateDeploymentRequestResourceTriggerV2Api(),

    startDate: faker.date.future(),
    description: faker.lorem.paragraph(),
  };
}

export function searchDeploymentRequestsBffV2CustomHandlerMocked(
  count: number,
  configurationType?: 'BOTH' | 'CONFIGURATION' | 'DELETE',
) {
  const configurationModules: SearchDeploymentRequestsBffV2MutationResponseApi = {
    pagination: generateInitialPaginationApi(count),
    data: faker.helpers.multiple(() => generateDeploymentRequestResourceItemBffV2(configurationType), { count }),
  };
  return configurationModules;
}

// eslint-disable-next-line @typescript-eslint/require-await
export async function searchDeploymentRequestsBffV2CustomHandler() {
  const count = faker.number.int({ min: 50, max: 100 });

  const configurationModules = searchDeploymentRequestsBffV2CustomHandlerMocked(count);

  return HttpResponse.json<SearchDeploymentRequestsBffV2MutationResponseApi>(configurationModules);
}

export function searchConfigurationsV2CustomHandlerCustomer(
  context: ResponseResolverInfo<HttpRequestResolverExtras<PathParams>>,
) {
  const totalResults: ConfigurationResourceItemV2Api[] = faker.helpers.multiple(
    (_, count) => buildConfigurationResourceItemV2Api(count),
    { count: 9 },
  );
  return HttpResponse.json<SearchConfigurationsV2MutationResponseApi>({
    pagination: {
      itemsPerPage: 10,
      startIndex: 0,
      totalResults: totalResults.length,
    },
    data: totalResults,
  });
}

function buildConfigurationResourceItemV2Api(num: number): ConfigurationResourceItemV2Api {
  return {
    creationDate: faker.date.recent(),
    updateDate: faker.date.recent(),
    creator: {
      id: faker.string.uuid(),
      // name: faker.person.firstName(), // this change can be impacted due upgrade on MDMGT-7043
    },
    updatedBy: {
      id: faker.string.uuid(),
    },
    id: `1234567${num}-1234-1234-1234-123456789012`,
    name: `conf${num}`,
    definition: {
      compositions: [{ configurationId: faker.string.uuid() }],
    },
  };
}
